import React, { useMemo } from "react";
import { Checkbox, Dialog, DialogActions, DialogContent, FormControlLabel, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import ButtonComponent from "components/button/ButtonComponent";
import Typography from "@mui/material/Typography";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { CheckboxGroupController, Option } from "components/form/CheckboxGroupController";

export type FormData = {
  maker_names: string[];
  winter_flag: string[];
  wheel_color_names: string[];
};

const styles = {
  label: {
    fontSize: { xs: "20px", md: "24px", lg: "28px" },
    fontWeight: "medium",
    color: "var(--black)",
  },
  box: {
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
  },
  checkbox: {
    width: "calc((100% - 32px)/3)",
    minWidth: "120px",
  },
};

interface FilterOptionModalProps {
  makerOptions: Option[];
  showFilterModal: boolean;
  setShowFilterModal: (show: boolean) => void;
  onHandleFilter: (data: FormData) => void;
  defaultValues: FormData;
}

/**
 * FilterOptionModal Component
 *
 * This component renders a modal for filtering options related to nut, lock nut, and hub ring colors.
 * It provides radio button groups for selecting options and a button to apply the filter.
 * @param {object} props - The props for the component.
 * @param {boolean} props.showFilterModal - Controls the visibility of the modal.
 * @param {(show: boolean) => void} props.setShowFilterModal - Callback function to toggle the modal visibility.
 */
export default function FilterOptionModal({
  showFilterModal,
  setShowFilterModal,
  onHandleFilter,
  defaultValues,
  makerOptions,
}: FilterOptionModalProps) {
  const { control, handleSubmit, setValue } = useForm<FormData>({
    defaultValues,
  });

  // Observe live maker_names value to compute isAllSelected / isAllDeselected
  const selectedMakerNames = useWatch({ control, name: "maker_names" });

  // Mirror FilterProcFeeModal: derive state from current selection vs. available options
  const isAllMakerSelected = useMemo(() => {
    if (!makerOptions || makerOptions.length === 0) return false;
    return makerOptions.every((opt) => selectedMakerNames?.includes(opt.value));
  }, [selectedMakerNames, makerOptions]);

  const isAllMakerDeselected = useMemo(() => {
    if (!makerOptions || makerOptions.length === 0) return true;
    return makerOptions.every((opt) => !selectedMakerNames?.includes(opt.value));
  }, [selectedMakerNames, makerOptions]);

  const handleSelectAllMakers = () => {
    setValue("maker_names", makerOptions.map((opt) => opt.value));
  };

  const handleDeselectAllMakers = () => {
    setValue("maker_names", []);
  };

  const handleClose = () => {
    setShowFilterModal(false);
  };

  const onSubmit: SubmitHandler<FormData> = (data) => {
    onHandleFilter(data);
    handleClose();
  };

  const flagOptions = [
    { label: "冬", value: "1" },
    { label: "超冬", value: "2" },
    { label: "それ以外", value: "3" },
  ];

  const colorOptions = [
    { label: "シルバー", value: "silver" },
    { label: "ブラック", value: "black" },
    { label: "ホワイト", value: "white" },
  ];

  return (
    <Dialog
      open={showFilterModal}
      onClose={handleClose}
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: "21px" },
      }}
      fullWidth
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 7, pl: { xs: 6, md: 8 } }}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              <Typography sx={styles.label}>メーカー</Typography>
              {/* 全選択 / 全解除 — same logic as FilterProcFeeModal */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2, pl: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAllMakerSelected}
                      onChange={(e) => {
                        if (e.target.checked) handleSelectAllMakers();
                        else handleDeselectAllMakers();
                      }}
                      color="secondary"
                      sx={{ p: 0, pr: 1 }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: { xs: 12, md: 16, lg: 18 }, color: "var(--black)", fontWeight: "regular" }}>
                      全選択
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAllMakerDeselected}
                      onChange={(e) => {
                        if (e.target.checked) handleDeselectAllMakers();
                        else handleSelectAllMakers();
                      }}
                      color="secondary"
                      sx={{ p: 0, pr: 1 }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: { xs: 12, md: 16, lg: 18 }, color: "var(--black)", fontWeight: "regular" }}>
                      全解除
                    </Typography>
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <CheckboxGroupController
                name="maker_names"
                control={control}
                options={makerOptions}
                sxBox={styles.box}
                sxLabel={styles.checkbox}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1} py={5}>
            <Grid item xs={12} md={3}>
              <Typography sx={styles.label}>フラグ</Typography>
            </Grid>
            <Grid item xs={12} md={9}>
              <CheckboxGroupController
                name="winter_flag"
                control={control}
                options={flagOptions}
                sxBox={styles.box}
                sxLabel={styles.checkbox}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              <Typography sx={styles.label}>カラー</Typography>
            </Grid>
            <Grid item xs={12} md={9}>
              <CheckboxGroupController
                name="wheel_color_names"
                control={control}
                options={colorOptions}
                sxBox={styles.box}
                sxLabel={styles.checkbox}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ py: 3, justifyContent: "center" }}>
          <ButtonComponent
            sx={{ fontSize: { xs: 16, md: 20, lg: 25 } }}
            label="絞り込み"
            type="submit"
            theme="dark"
            disabled={isAllMakerDeselected}
          />
        </DialogActions>
      </form>
    </Dialog>
  );
}
